import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { COLLECTIONS, HIDDEN_FIELDS } from "@/admin/config";
import FieldEditor from "@/admin/FieldEditor";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, X, Sparkles } from "lucide-react";

export default function CollectionManager({ collection }) {
  const cfg = COLLECTIONS[collection];
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // item object or null
  const [aiOpen, setAiOpen] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin", collection],
    queryFn: async () => (await api.get(`/admin/${collection}`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async (item) => {
      if (item.id) return (await api.put(`/admin/${collection}/${item.id}`, item)).data;
      return (await api.post(`/admin/${collection}`, item)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", collection] }); setEditing(null); toast.success("Tersimpan"); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/${collection}/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", collection] }); toast.success("Dihapus"); },
  });

  const reorderMut = useMutation({
    mutationFn: async (ids) => (await api.post(`/admin/${collection}/reorder`, { ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", collection] }),
  });

  const move = (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorderMut.mutate(arr.map((x) => x.id));
  };

  const title = (it) => it[cfg.titleKey] || it.name || it.title || "(tanpa judul)";

  return (
    <div data-testid={`manager-${collection}`}>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display font-black tracking-tightest text-3xl md:text-4xl">{cfg.label}</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} item</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {cfg.ai && <button onClick={() => setAiOpen(true)} className="btn-grid-outline inline-flex min-h-11 items-center gap-2 rounded-full px-4 py-2.5 overline text-[10px]" data-testid="ai-generate-btn"><Sparkles className="w-3.5 h-3.5" /> AI Artikel</button>}
          {!cfg.noCreate && <button onClick={() => setEditing({ ...structuredClone(cfg.template) })} className="btn-primary inline-flex min-h-11 items-center gap-2 rounded-full px-4 py-2.5 overline text-[10px]" data-testid="create-btn"><Plus className="w-3.5 h-3.5" /> Tambah</button>}
        </div>
      </div>

      {isLoading ? <p className="overline text-xs text-muted-foreground">Memuat…</p> : (
        <div className="border border-border divide-y divide-border">
          {items.map((it, idx) => (
            <div key={it.id} className="group flex items-center gap-2 px-3 py-3 hover:bg-secondary/50 sm:gap-4 sm:px-4" data-testid={`row-${collection}-${idx}`}>
              {it.cover_image || it.hero_image || it.photo || it.logo || it.image || it.thumbnail ? (
                <img src={it.cover_image || it.hero_image || it.photo || it.logo || it.image || it.thumbnail} alt="" className="w-10 h-10 object-cover border border-border shrink-0" />
              ) : <div className="w-10 h-10 border border-border shrink-0 flex items-center justify-center overline text-[9px] text-muted-foreground">{String(idx + 1).padStart(2, "0")}</div>}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{title(it)}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {it.status && <span className={`overline text-[9px] mr-2 ${it.status === "published" ? "text-emerald-600" : "text-amber-600"}`}>{it.status}</span>}
                  {it.flagship && <span className="overline text-[9px] mr-2">✦ flagship</span>}
                  {it.slug || it.url || it.category || ""}
                  {it.is_demo && <span className="overline text-[9px] ml-2 text-amber-600">DEMO</span>}
                  {typeof it.published === "boolean" && !it.status && <span className={`overline text-[9px] ml-2 ${it.published ? "text-emerald-600" : "text-muted-foreground"}`}>{it.published ? "published" : "hidden"}</span>}
                </p>
              </div>
              {cfg.reorder && (
                <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => move(idx, -1)} className="p-0.5 hover:text-foreground text-muted-foreground"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} className="p-0.5 hover:text-foreground text-muted-foreground"><ChevronDown className="w-4 h-4" /></button>
                </div>
              )}
              <button onClick={() => setEditing({ ...it })} className="flex h-11 w-11 shrink-0 items-center justify-center hover:bg-secondary" aria-label={`Edit ${title(it)}`} data-testid={`edit-${idx}`}><Pencil className="w-4 h-4" /></button>
              {!cfg.noDelete && <button onClick={() => { if (window.confirm("Hapus item ini?")) delMut.mutate(it.id); }} className="flex h-11 w-11 shrink-0 items-center justify-center hover:bg-destructive hover:text-destructive-foreground" aria-label={`Hapus ${title(it)}`} data-testid={`delete-${idx}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {items.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground">Belum ada data.</p>}
        </div>
      )}

      {editing && <EditDrawer item={editing} onClose={() => setEditing(null)} onSave={(it) => saveMut.mutate(it)} saving={saveMut.isPending} label={cfg.singular} />}
      {aiOpen && <AiDrawer onClose={() => setAiOpen(false)} onGenerated={(draft) => { setAiOpen(false); setEditing({ ...structuredClone(cfg.template), ...draft }); }} />}
    </div>
  );
}

function EditDrawer({ item, onClose, onSave, saving, label }) {
  const [form, setForm] = useState(item);
  const keys = Object.keys(form).filter((k) => !HIDDEN_FIELDS.includes(k));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="edit-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background border-l border-border h-full overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-background px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="min-w-0 truncate font-display text-lg font-bold sm:text-xl">{form.id ? "Edit" : "Tambah"} {label}</h2>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={() => onSave(form)} disabled={saving} className="btn-primary rounded-full px-5 py-2.5 overline text-[10px] disabled:opacity-50" data-testid="save-btn">{saving ? "Menyimpan…" : "Simpan"}</button>
            <button onClick={onClose} className="flex h-11 w-11 items-center justify-center hover:bg-secondary" aria-label="Tutup editor"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="space-y-5 p-4 sm:p-6">
          {keys.map((k) => <FieldEditor key={k} fieldKey={k} value={form[k]} onChange={setField(k)} />)}
        </div>
      </div>
    </div>
  );
}

function AiDrawer({ onClose, onGenerated }) {
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    if (!topic.trim()) return toast.error("Isi topik artikel");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai/generate-article", { topic, keyword });
      toast.success("Draft dibuat!");
      onGenerated({ title: data.title || topic, excerpt: data.excerpt || "", content: data.content || "", primary_keyword: keyword, seo_title: (data.title || topic) + " | Digitalis", seo_description: (data.excerpt || "").slice(0, 155) });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="ai-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-background border border-border p-8">
        <div className="flex items-center gap-2 mb-6"><Sparkles className="w-5 h-5" /><h2 className="font-display font-bold text-xl">Generate Artikel dengan AI</h2></div>
        <div className="space-y-4">
          <div><label className="overline text-[10px] text-muted-foreground">Topik Artikel</label><input className="w-full bg-background border border-border px-3 py-2 text-sm" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="mis. Tips Instagram untuk UMKM Medan" data-testid="ai-topic" /></div>
          <div><label className="overline text-[10px] text-muted-foreground">Target Keyword (opsional)</label><input className="w-full bg-background border border-border px-3 py-2 text-sm" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="mis. social media UMKM Medan" data-testid="ai-keyword" /></div>
          <div className="flex gap-2 pt-2">
            <button onClick={gen} disabled={loading} className="btn-primary rounded-full px-6 py-3 overline text-[10px] disabled:opacity-50" data-testid="ai-submit">{loading ? "Membuat…" : "Generate Draft"}</button>
            <button onClick={onClose} className="btn-grid-outline rounded-full px-6 py-3 overline text-[10px]">Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}
